import { QueryClient, useQueryClient } from "react-query";
import { useCopies } from "shared/hooks/useCopies";
import MyExpApi from "apps/myExperience/enum/MyExpApi";
import { successToast } from "shared/utils/toastNotification";
import { ApiActivitySelectionRequest } from "shared/types/ApiActivitySelectionRequest";
import { getApiQueryKey, useDeleteApiData, useGetApiData, usePostApiData } from "shared/datalayer";
import { ApiActivitySelectionForParticipantResponse } from "shared/types/ApiActivitySelectionForParticipantResponse";

export function getActivitiesForParticipantQueryKey(programId?: string, eventParticipantId?: string) {
  return getApiQueryKey(MyExpApi.ASSOCIATED_PROGRAM_ACTIVITY_LIST_BY_EVENT_PARTICIPANT_AND_PROGRAM_ID, {
    params: { programId, eventParticipantId }
  });
}

export function useGetActivitiesForParticipant({
  programId,
  eventParticipantId
}: {
  programId?: string;
  eventParticipantId?: string;
}) {
  return useGetApiData<Array<ApiActivitySelectionForParticipantResponse>>(
    MyExpApi.ASSOCIATED_PROGRAM_ACTIVITY_LIST_BY_EVENT_PARTICIPANT_AND_PROGRAM_ID,
    {
      params: { programId, eventParticipantId }
    },
    { enabled: Boolean(programId) && Boolean(eventParticipantId) }
  );
}

export function useCreateSelectionRequest({
  programId,
  eventParticipantId
}: {
  programId: string;
  eventParticipantId: string;
}) {
  const copies = useCopies();
  const queryClient = useQueryClient();

  return usePostApiData<ApiActivitySelectionForParticipantResponse, ApiActivitySelectionRequest>(
    MyExpApi.ASSOCIATED_PROGRAM_ACTIVITY_SELECTION_REQUEST_CREATE,
    {
      onSuccess: data => {
        successToast(copies.get("associate.program.msg.selection.created"));
        updateActivityListForParticipantWithChanges(queryClient, programId, eventParticipantId, data);
      }
    }
  );
}

export function useUpdateSelectionRequest({
  programId,
  eventParticipantId
}: {
  programId: string;
  eventParticipantId: string;
}) {
  const copies = useCopies();
  const queryClient = useQueryClient();
  return usePostApiData<ApiActivitySelectionForParticipantResponse, ApiActivitySelectionRequest>(
    MyExpApi.ASSOCIATED_PROGRAM_ACTIVITY_SELECTION_REQUEST_UPDATE,
    {
      onSuccess: data => {
        successToast(copies.get("associate.program.msg.selection.updated"));
        updateActivityListForParticipantWithChanges(queryClient, programId, eventParticipantId, data);
      }
    }
  );
}

export function updateActivityListForParticipantWithChanges(
  queryClient: QueryClient,
  programId: string,
  eventParticipantId: string,
  dataToUpdate: ApiActivitySelectionForParticipantResponse
) {
  queryClient.setQueryData(
    getActivitiesForParticipantQueryKey(programId, eventParticipantId),
    (prevData: Array<ApiActivitySelectionForParticipantResponse> = []) => {
      return (prevData as Array<ApiActivitySelectionForParticipantResponse>).map(item => {
        return item.activityId === dataToUpdate.activityId ? { ...dataToUpdate } : { ...item };
      });
    }
  );
}

export function useDeleteSelectionRequest({
  programId,
  eventParticipantId
}: {
  programId: string;
  eventParticipantId: string;
}) {
  const copies = useCopies();
  const queryClient = useQueryClient();
  return useDeleteApiData(MyExpApi.ASSOCIATED_PROGRAM_ACTIVITY_SELECTION_REQUEST_DELETE, {
    onSuccess: () => {
      successToast(copies.get("associate.program.msg.selection.deleted"));
      queryClient.invalidateQueries(getActivitiesForParticipantQueryKey(programId, eventParticipantId));
    }
  });
}
